<template>
  <form
    ref="form"
    method="post"
    novalidate
    @submit.prevent="handleSubmit"
  >
    <FormProblemBanner
      v-if="problem !== null && FormProblemState.isPresentable(problem)"
      :problem="problem"
    />

    <slot />
  </form>
</template>

<script setup lang="ts">
import { useTemplateRef, watch } from 'vue'
import type { FormHTMLAttributes } from 'vue'

import { FormProblemState } from '~/services/forms/FormProblemState'

import FormProblemBanner from './FormProblemBanner.molecule.vue'

const props = withDefaults(
  defineProps<{
    /** Native form submission callback. */
    onSubmit?: FormHTMLAttributes['onSubmit']
    /** The in-flight form validation problem, if any. */
    problem?: FormProblemState | null
  }>(),
  { onSubmit: undefined, problem: null }
)

defineSlots<{
  default?: () => unknown
}>()

const handleSubmit: NonNullable<FormHTMLAttributes['onSubmit']> = (event) => props.onSubmit?.(event)

const form = useTemplateRef<HTMLFormElement>('form')

const SCROLL_TO_CENTER: ScrollIntoViewOptions = { behavior: 'smooth', block: 'center' }

const FOCUSABLE_CONTROL =
  'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'

/** The invalid element itself when focusable, else its first focusable control, e.g. the first radio of an invalid radio group. */
const focusTargetOf = (invalid: Element): Element | null =>
  invalid.matches(FOCUSABLE_CONTROL) ? invalid : invalid.querySelector(FOCUSABLE_CONTROL)

/** Brings a fresh verdict into view: the banner first, then focus onto the first invalid control without scrolling away from it. */
const presentProblem = (): void => {
  form.value?.querySelector('[data-form-problem-banner]')?.scrollIntoView(SCROLL_TO_CENTER)
  const firstInvalid = form.value?.querySelector('[aria-invalid="true"]')
  const target = firstInvalid instanceof Element ? focusTargetOf(firstInvalid) : null
  if (target instanceof HTMLElement) {
    target.focus({ preventScroll: true })
  }
}

// Keyed on the verdict object, which changes per submit attempt; its live fieldCount updating never re-scrolls.
watch(
  () => props.problem,
  (problem) => {
    if (problem !== null) presentProblem()
  },
  { flush: 'post' }
)
</script>
